import { useId } from "react";

export interface Series {
  label?: string;
  data: number[];
  color?: string;
}

interface Props {
  series: Series[];
  height?: number;
  yMax?: number;
  yMin?: number;
  yTicks?: number;
  xTicks?: number;
  unit?: string;
  colors?: string[];
  showGrid?: boolean;
  showAxis?: boolean;
  smooth?: boolean;
}

export function AreaChart({
  series,
  height = 140,
  yMax,
  yMin = 0,
  yTicks = 4,
  xTicks = 6,
  unit = "",
  colors = ["var(--cyan)", "var(--violet)", "var(--amber)"],
  showGrid = true,
  showAxis = true,
  smooth = true,
}: Props) {
  const rawId = useId();
  const baseId = `ac-${rawId.replace(/:/g, "")}`;

  const padL = showAxis ? 34 : 4;
  const padR = 8;
  const padT = 8;
  const padB = showAxis ? 20 : 4;
  const w = 600;
  const h = height;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const n = series[0]?.data?.length ?? 0;
  if (!n) return <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height }} />;

  const flat = series.flatMap((s) => s.data);
  const computedMax = yMax ?? (Math.max(...flat) * 1.15 || 1);
  const step = innerW / (n - 1 || 1);
  const yScale = (v: number) => padT + innerH - ((v - yMin) / (computedMax - yMin)) * innerH;
  const mkPath = (data: number[]) =>
    data
      .map((v, i) => {
        const x = padL + i * step;
        const y = yScale(v);
        if (i === 0) return `M${x.toFixed(2)},${y.toFixed(2)}`;
        if (smooth) {
          const px = padL + (i - 1) * step;
          const py = yScale(data[i - 1]);
          const cx = (px + x) / 2;
          return `C${cx.toFixed(2)},${py.toFixed(2)} ${cx.toFixed(2)},${y.toFixed(2)} ${x.toFixed(2)},${y.toFixed(2)}`;
        }
        return `L${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");

  const ids = series.map((_, i) => `${baseId}-${i}`);

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      style={{ width: "100%", height, display: "block" }}
      preserveAspectRatio="none"
    >
      <defs>
        {series.map((s, i) => (
          <linearGradient key={i} id={ids[i]} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={s.color ?? colors[i]} stopOpacity="0.35" />
            <stop offset="100%" stopColor={s.color ?? colors[i]} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
      {showGrid &&
        Array.from({ length: yTicks + 1 }).map((_, i) => {
          const y = padT + (innerH / yTicks) * i;
          const v = computedMax - ((computedMax - yMin) / yTicks) * i;
          return (
            <g key={i}>
              <line
                x1={padL} x2={w - padR} y1={y} y2={y}
                stroke="var(--line)" strokeWidth="1" vectorEffect="non-scaling-stroke"
              />
              {showAxis && (
                <text
                  x={padL - 6} y={y + 3}
                  fontSize="9" fill="var(--fg-3)" textAnchor="end"
                  fontFamily="var(--mono)"
                >
                  {Math.round(v)}
                  {unit}
                </text>
              )}
            </g>
          );
        })}
      {showAxis &&
        Array.from({ length: xTicks + 1 }).map((_, i) => {
          const x = padL + (innerW / xTicks) * i;
          const secondsAgo = Math.round(((xTicks - i) / xTicks) * (n - 1));
          return (
            <text
              key={i} x={x} y={h - 6}
              fontSize="9" fill="var(--fg-3)" textAnchor="middle"
              fontFamily="var(--mono)"
            >
              {secondsAgo === 0 ? "now" : `-${secondsAgo}s`}
            </text>
          );
        })}
      {series.map((s, i) => {
        const path = mkPath(s.data);
        const area = `${path} L${(padL + (n - 1) * step).toFixed(2)},${padT + innerH} L${padL},${padT + innerH} Z`;
        return (
          <g key={i}>
            <path d={area} fill={`url(#${ids[i]})`} />
            <path
              d={path} fill="none"
              stroke={s.color ?? colors[i]} strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        );
      })}
    </svg>
  );
}
